// Centralized Meta/Facebook Pixel tracking utility.
// Splits init into two phases:
//   1. Lightweight fbq stub created on module load — queues calls natively (~tiny)
//   2. Heavy fbevents.js script loaded on first user interaction OR after idle
// This keeps the initial bundle/network light and improves LCP/TBT.

const PIXEL_ID = '1215304437119773'
const COMMON = { content_category: 'Nutrición Resuelta' }

type Params = Record<string, any>

let stubReady = false
let scriptStarted = false

function getFbq(): any {
  return typeof window !== 'undefined' ? (window as any).fbq : undefined
}

/** Create the fbq stub synchronously. Queues calls until fbevents.js loads. */
function ensureStub() {
  if (stubReady || typeof window === 'undefined') return
  stubReady = true
  const w = window as any
  if (!w.fbq) {
    const n: any = (w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!w._fbq) w._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
  }
  try {
    w.fbq('init', PIXEL_ID)
    w.fbq('track', 'PageView')
  } catch {}
}

/** Lazy-load the actual fbevents.js script. Idempotent. */
function loadScript() {
  if (scriptStarted || typeof window === 'undefined') return
  scriptStarted = true
  const t = document.createElement('script')
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const s = document.getElementsByTagName('script')[0]
  if (s && s.parentNode) {
    s.parentNode.insertBefore(t, s)
  } else {
    document.head.appendChild(t)
  }
}

/** Init pixel: stub now, script deferred to first interaction or idle. */
export function initPixel() {
  if (typeof window === 'undefined') return
  ensureStub()
  if (scriptStarted) return

  let triggered = false
  const trigger = () => {
    if (triggered) return
    triggered = true
    loadScript()
    window.removeEventListener('pointerdown', trigger)
    window.removeEventListener('touchstart', trigger)
    window.removeEventListener('keydown', trigger)
    window.removeEventListener('scroll', trigger)
  }
  window.addEventListener('pointerdown', trigger, { passive: true, once: true })
  window.addEventListener('touchstart', trigger, { passive: true, once: true })
  window.addEventListener('keydown', trigger, { passive: true, once: true })
  window.addEventListener('scroll', trigger, { passive: true, once: true })

  // Fallback: load after idle (3.5s) even without interaction
  if ('requestIdleCallback' in window) {
    ;(window as any).requestIdleCallback(trigger, { timeout: 3500 })
  } else {
    setTimeout(trigger, 3500)
  }
}

/** Standard Meta Pixel event */
export function track(event: string, data?: Params) {
  ensureStub()
  const fbq = getFbq()
  if (!fbq) return
  try {
    fbq('track', event, { ...COMMON, ...(data || {}) })
  } catch {}
}

/** Custom (non-standard) event */
export function trackCustom(name: string, data?: Params) {
  ensureStub()
  const fbq = getFbq()
  if (!fbq) return
  try {
    fbq('trackCustom', name, { ...COMMON, ...(data || {}) })
  } catch {}
}

/** Hook: fire ViewContent (standard) when section enters viewport */
import { useEffect, useRef } from 'react'
export function useSectionView(name: string, threshold = 0.3) {
  const ref = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!ref.current) return
    const el = ref.current
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          track('ViewContent', { content_name: name })
          observer.disconnect()
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [name, threshold])
  return ref
}

/** Setup global trackers: scroll depth, time on page, exit intent, first interaction */
export function setupGlobalTracking() {
  if (typeof window === 'undefined') return
  if ((window as any).__mpTrackingSetup) return
  ;(window as any).__mpTrackingSetup = true

  initPixel()

  // ── Scroll depth ──
  const milestones = [25, 50, 75, 90]
  const fired = new Set<number>()
  let scrollRaf = 0
  const onScroll = () => {
    const doc = document.documentElement
    const scrollTop = window.scrollY || doc.scrollTop
    const height = doc.scrollHeight - doc.clientHeight
    if (height <= 0) return
    const pct = Math.round((scrollTop / height) * 100)
    milestones.forEach((m) => {
      if (pct >= m && !fired.has(m)) {
        fired.add(m)
        trackCustom('ScrollDepth', { depth: m, percent: pct })
      }
    })
    if (fired.size === milestones.length) {
      window.removeEventListener('scroll', rafScroll)
    }
  }
  const rafScroll = () => {
    if (scrollRaf) return
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0
      onScroll()
    })
  }
  window.addEventListener('scroll', rafScroll, { passive: true })

  // ── Time on page milestones ──
  const timeMs = [15, 30, 60, 120, 240]
  timeMs.forEach((s) => {
    setTimeout(() => trackCustom('TimeOnPage', { seconds: s }), s * 1000)
  })

  // ── First user interaction (engagement) ──
  let interacted = false
  const markInteracted = () => {
    if (interacted) return
    interacted = true
    trackCustom('FirstInteraction')
    window.removeEventListener('click', markInteracted)
    window.removeEventListener('touchstart', markInteracted)
    window.removeEventListener('keydown', markInteracted)
  }
  window.addEventListener('click', markInteracted, { passive: true })
  window.addEventListener('touchstart', markInteracted, { passive: true })
  window.addEventListener('keydown', markInteracted, { passive: true })

  // ── Exit intent (desktop) ──
  let exitFired = false
  const onMouseOut = (e: MouseEvent) => {
    if (exitFired) return
    if (e.clientY <= 0) {
      exitFired = true
      trackCustom('ExitIntent')
      document.removeEventListener('mouseout', onMouseOut)
    }
  }
  document.addEventListener('mouseout', onMouseOut)

  // ── Page hide / unload ──
  const start = Date.now()
  const onHide = () => {
    const secs = Math.round((Date.now() - start) / 1000)
    trackCustom('SessionEnd', { seconds: secs })
  }
  window.addEventListener('pagehide', onHide, { once: true })
}
